import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { CustomButton } from "../../../common/ReusableComponent/CustomButton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from "recharts";
import {
  FiFileText,
  FiDollarSign,
  FiShoppingCart,
  FiTruck,
  FiClock,
} from "react-icons/fi";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];

const AssetProcurementDashboard = () => {
  const { Get } = useApi();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  const [summaryStats, setSummaryStats] = useState([
    { label: "Active RFQs", value: 0, color: "blue", icon: <FiFileText /> },
    { label: "Pending Quotations", value: 0, color: "orange", icon: <FiDollarSign /> },
    { label: "Active POs", value: 0, color: "green", icon: <FiShoppingCart /> },
    { label: "Assets Received", value: 0, color: "purple", icon: <FiTruck /> },
  ]);

  const [rfqStatusData, setRfqStatusData] = useState([]);
  const [quotationTrendData, setQuotationTrendData] = useState([]);
  const [poStatusData, setPoStatusData] = useState([]);
  const [monthlySpendData, setMonthlySpendData] = useState([]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await Get("asset-procurement-dashboard");
      
      setDashboardData(data);

      // Update summary stats
      setSummaryStats([
        {
          label: "Active RFQs",
          value: data.rfq_stats.active || 0,
          color: "blue",
          icon: <FiFileText />,
        },
        {
          label: "Pending Quotations",
          value: data.quotation_stats.pending || 0,
          color: "orange",
          icon: <FiDollarSign />,
        },
        {
          label: "Active POs",
          value: data.po_stats.active || 0,
          color: "green",
          icon: <FiShoppingCart />,
        },
        {
          label: "Assets Received",
          value: data.assets_received.total || 0,
          color: "purple",
          icon: <FiTruck />,
        },
      ]);

      // Set chart data
      setRfqStatusData(data.rfq_status_breakdown || []);
      setQuotationTrendData(data.quotation_trend || []);
      setPoStatusData(data.po_status_breakdown || []);
      setMonthlySpendData(data.monthly_spend || []);

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const getNoDataMessage = () => {
    return dashboardData?.total_rfqs === 0
      ? "No procurement activities found. Start by creating your first RFQ."
      : null;
  };

  const quickActions = [
    {
      title: "Create RFQ",
      description: "Start a new Request for Quotation",
      action: () => navigate("/rfq-management"),
      color: "#3b82f6"
    },
    {
      title: "Review Quotations",
      description: "Review and approve vendor quotations",
      action: () => navigate("/quotation-management"),
      color: "#f59e0b"
    },
    {
      title: "Manage POs",
      description: "Create and track Purchase Orders",
      action: () => navigate("/purchase-order-management"),
      color: "#10b981"
    },
    {
      title: "Asset Inventory",
      description: "View received assets inventory",
      action: () => navigate("/assets-inventory"),
      color: "#8b5cf6"
    }
  ];

  return (
    <div className="responsive-box">
      {loading && <p>Loading...</p>}

      {!loading && dashboardData && getNoDataMessage() && (
        <div className="no-data-message">
          <p>{getNoDataMessage()}</p>
        </div>
      )}

      {!loading && dashboardData && !getNoDataMessage() && (
        <>
          {/* Summary Statistics */}
          <div className="asset-summary-row">
            {summaryStats.map((item, index) => (
              <div className="summary-card" key={index}>
                <div className={`summary-icon ${item.color}`}>
                  {item.icon}
                </div>
                <div className="summary-content">
                  <p className="summary-label">{item.label}</p>
                  <h2 className="summary-value">{item.value}</h2>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="quick-actions-section" style={{ marginBottom: "24px" }}>
            <h4 style={{ marginBottom: "16px" }}>Quick Actions</h4>
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", 
              gap: "16px" 
            }}>
              {quickActions.map((action, index) => (
                <div
                  key={index}
                  onClick={action.action}
                  style={{
                    padding: "16px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    backgroundColor: "#ffffff"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = action.color;
                    e.target.style.boxShadow = `0 4px 12px ${action.color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <h6 style={{ color: action.color, marginBottom: "8px" }}>{action.title}</h6>
                  <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>{action.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Charts Row 1 */}
          <div className="chart-row">
            <div className="chart-card">
              <h4 style={{ marginBottom: 16 }}>RFQ Status Breakdown</h4>
              <ResponsiveContainer width="100%" height="85%">
                <PieChart>
                  <Pie
                    data={rfqStatusData}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ status, count }) => `${status}: ${count}`}
                  >
                    {rfqStatusData.map((_, index) => (
                      <Cell
                        key={index}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h4 style={{ marginBottom: 16 }}>Purchase Order Status</h4>
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={poStatusData} barGap={8}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="status" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="count"
                    name="Count"
                    fill="#3b82f6"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="chart-row">
            <div className="chart-card">
              <h4 style={{ marginBottom: 16 }}>Monthly Procurement Spend</h4>
              <ResponsiveContainer width="100%" height="85%">
                <LineChart data={monthlySpendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`$${value}`, "Amount"]} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={{ fill: "#10b981", strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h4 style={{ marginBottom: 16 }}>Quotation Response Trend</h4>
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={quotationTrendData} barGap={8}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="week" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="received"
                    name="Received"
                    fill="#3b82f6"
                    radius={[6, 6, 0, 0]}
                  />
                  <Bar
                    dataKey="approved"
                    name="Approved"
                    fill="#10b981"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Activities Table */}
          <div className="table-card">
            <h4 className="table-title">Recent Procurement Activities</h4>
            <table className="asset-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Number</th>
                  <th>Vendor</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {(dashboardData.recent_activities || []).map((activity, index) => (
                  <tr key={index}>
                    <td>
                      <span className={`pill ${activity.type.toLowerCase()}`}>
                        {activity.type}
                      </span>
                    </td>
                    <td>{activity.number}</td>
                    <td>{activity.vendor_name}</td>
                    <td>${parseFloat(activity.amount || 0).toFixed(2)}</td>
                    <td>
                      <span className={`pill ${activity.status.replace(/_/g, '-')}`}>
                        {activity.status.replace(/_/g, " ").toUpperCase()}
                      </span>
                    </td>
                    <td>{new Date(activity.date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pending Actions */}
          <div className="table-card">
            <h4 className="table-title">Pending Actions</h4>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px" }}>
              {(dashboardData.pending_actions || []).map((action, index) => (
                <div
                  key={index}
                  style={{
                    padding: "16px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    backgroundColor: "#f9fafb"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                    <FiClock style={{ color: "#f59e0b", marginRight: "8px" }} />
                    <h6 style={{ margin: 0 }}>{action.title}</h6>
                  </div>
                  <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "12px" }}>
                    {action.description}
                  </p>
                  <CustomButton
                    text={action.action_text}
                    onClick={() => navigate(action.action_url)}
                    color="#ffffff"
                    backgroundColor="#f59e0b"
                    style={{ fontSize: "12px", padding: "6px 12px" }}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AssetProcurementDashboard;